import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Outlet } from "react-router-dom";
import { Navbar, Footer, MobileBottomNav } from "@/components/layout/index";
import { User } from "lucide-react";
import { getTrackedJobs } from "@/lib/api";
import { getAccessToken } from "@/lib/authToken";
import { CheckoutSuccessSync } from "@/components/billing/CheckoutSuccessSync";
import { PaywallProvider } from "@/components/paywall/PaywallContext";
import { EntitlementProvider } from "@/contexts/EntitlementContext";

function useMobileNavPortal() {
  const [portalNavToBody, setPortalNavToBody] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setPortalNavToBody(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return portalNavToBody;
}

export default function Layout() {
  const [trackedCount, setTrackedCount] = useState(0);
  const portalNavToBody = useMobileNavPortal();

  useEffect(() => {
    let mounted = true;
    const loadCount = async () => {
      if (!getAccessToken()) {
        if (mounted) setTrackedCount(0);
        return;
      }
      try {
        const list = await getTrackedJobs();
        if (mounted) setTrackedCount(Array.isArray(list) ? list.length : 0);
      } catch {
        if (mounted) setTrackedCount(0);
      }
    };
    loadCount();

    const onTrackerChanged = (e) => {
      const delta = Number(e?.detail?.delta || 0);
      if (delta !== 0) {
        setTrackedCount((c) => Math.max(0, c + delta));
      } else {
        loadCount();
      }
    };

    window.addEventListener("tracker:changed", onTrackerChanged);
    return () => {
      mounted = false;
      window.removeEventListener("tracker:changed", onTrackerChanged);
    };
  }, []);

  const navItems = useMemo(() => ([
    { to: "/jobs", label: "Find Jobs" },
    { to: "/track", label: "Track Jobs", badge: trackedCount },
    { to: "/tools", label: "Tools" },
    { to: "/pricing", label: "Pricing" },
  ]), [trackedCount]);

  const navbar = (
    <Navbar
      logoMarkSrc={`${process.env.PUBLIC_URL}/logo-mark.png`}
      brandLabel="RareRoles"
      navItems={navItems}
      accountTo="/account"
      accountLabel="Account"
      accountIcon={User}
    />
  );

  return (
    <EntitlementProvider>
      <PaywallProvider>
      <CheckoutSuccessSync />
      <div className="flex min-h-screen flex-col bg-background">
        {/*
          Mobile: navbar is fixed; reserve its height so content is not covered.
          Uses .h-mobile-navbar-spacer (constant + env safe-area) to match Navbar padding.
        */}
        <div
          className="hidden max-md:block max-md:shrink-0 max-md:w-full max-md:h-mobile-navbar-spacer"
          aria-hidden
        />
        {portalNavToBody
          ? createPortal(navbar, document.body)
          : navbar}
        <main className="flex min-h-0 flex-1 flex-col max-md:min-w-0 max-md:overflow-x-clip max-md:pb-mobile-bottom-nav">
          <Outlet />
        </main>
        {portalNavToBody
          ? createPortal(<MobileBottomNav trackBadge={trackedCount} />, document.body)
          : <MobileBottomNav trackBadge={trackedCount} />}
        <Footer />
      </div>
      </PaywallProvider>
    </EntitlementProvider>
  );
}
